import * as React from 'react';
import { connect } from 'react-redux';
import { QueryTable, QueryTableColumn } from "../../../misc/components/queryTable/QueryTable";

import { formatDateTime, formatNull } from "../../../misc/components/Formatters/Formatters";
import {
    Query,
    Permission,
    SpirometryAnalysisSet,
    User,
    OverreadStatusIdEnum,
    ChallengeDecisionMaker
} from "../../../misc/api/apiTypes";
import { Authorization } from "../../../misc/components/Authorization";
import { HamburgerMenu } from '../../../misc/components/HamburgerMenu';
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';
import { MainState } from '../../../Core/reducers';

import SiteIdFilterModal, { SiteIdHeader } from "../../Shared/FilterModals/SiteIdFilterModal";
import SubjectIdFilterModal, { SubjectIdHeader } from '../../Shared/FilterModals/SubjectIdFilterModal';
import OverreaderFilterModal, { OverreaderHeader } from "../../Shared/FilterModals/OverreaderFilterModal";
import StatusFilterModal, { StatusHeader } from "../../Shared/FilterModals/StatusFilterModal";
import CreatedAtFilterModal, { CreatedAtFilterMember, CreatedAtHeader } from "../../Shared/FilterModals/CreatedAtFilterModal";
import StartedAtFilterModal, { StartedAtFilterMember, StartedAtHeader } from "../../Shared/FilterModals/AnalysisSets/StartedAtFilterModal";
import AnalysisSetTypeFilterModal, { AnalysisSetTypeHeader, AnalysisSetTypeFilterMember } from "../../Shared/FilterModals/AnalysisSets/AnalysisSetTypeFilterModal";
import { QueryTableState, ReceiveQueryAction, PageAction, SortAction, QueryTableActions, FilterAction } from "../../../misc/components/queryTable/Hooks/UseQueryTable";
import Condition from '../../../misc/components/Condition';
import { SpirometryAnalysisSetTypeEnum } from '../../../misc/api/ApiSpirometryAnalysisSetType';
import { AssignToUserInGroupModal } from '../../../misc/components/AssignToUserInGroupModal';
import VisitFilterModal, { VisitFilterMember, VisitHeader } from '../../Shared/FilterModals/VisitFilterModal';
import SessionFilterModal, { SessionFilterMember, SessionHeader } from '../../Shared/FilterModals/SessionFilterModal';
import { Link } from 'react-router-dom';
import { GetAnalysisStatus } from '../../Shared/GetAnalysisStatus';
import { useSecurityContext } from "../../../Core/SecurityContext";
import SubjectTypeFilterModal, { SubjectTypeHeader } from '../../Shared/FilterModals/SubjectTypeFilterModal';
import { useCurrentStudyConfigurationContext } from '../../../Core/CurrentStudyConfigurationContext';
import { useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';

interface SpirometryAnalysisSetListTableOwnProps {
    queryTableReducer: [QueryTableState, React.Dispatch<QueryTableActions>];
}

interface SpirometryAnalysisSetListTableStateProps {
    currentUser: User;
}

interface SpirometryAnalysisSetListTableProps extends SpirometryAnalysisSetListTableStateProps, SpirometryAnalysisSetListTableOwnProps {

}

const SpirometryAnalysisSetListTable = ({ queryTableReducer, currentUser }: SpirometryAnalysisSetListTableProps): JSX.Element => {

    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const ViewOverreaderName = () => {

        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));

       
    }


    const RenderHamburgerMenu = (spirometryAnalysisSet: SpirometryAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User, overreadEnabled: boolean) => {
        return (
            <HamburgerMenu>
                <Authorization permissionId={PermissionEnum.AccessSingleFvcSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <Condition test={() => (spirometryAnalysisSet.Type.Id !== SpirometryAnalysisSetTypeEnum.QC) || (spirometryAnalysisSet.Type.Id === SpirometryAnalysisSetTypeEnum.QC && spirometryAnalysisSet.Analyst.Id === currentUser.Id)}>
                    <li>
                        <Link to={`/Spirometry/Analysis/${spirometryAnalysisSet.Id}/view`}>View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadFvcSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>

                    {overreadEnabled && (!spirometryAnalysisSet || !spirometryAnalysisSet.Analyst || !spirometryAnalysisSet.Analyst.Id || spirometryAnalysisSet.Analyst.Id === currentUser.Id) ?
                        <Condition test={() => spirometryAnalysisSet.Type.Id !== SpirometryAnalysisSetTypeEnum.Site}>
                            <li>
                                <Link to={`/Spirometry/Analysis/${spirometryAnalysisSet.Id}/overread`}>Over-read</Link>
                            </li>
                        </Condition>
                        : null}
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateAQCFvcAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <Condition test={() => spirometryAnalysisSet.Type.Id === SpirometryAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(spirometryAnalysisSet.SessionId); }}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadFvcSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => spirometryAnalysisSet.Type.Id === SpirometryAnalysisSetTypeEnum.Overread && spirometryAnalysisSet.Status.Id === OverreadStatusIdEnum.Pending}>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(spirometryAnalysisSet.Id); setReassignToUserModalVisible(true); }}>Reassign</a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    const refreshData = () => {
        setIsLoading(true);

        //Load the relevant sessions
        legacyClient.getSpirometryAnalysisSets(queryState.query).then((_populatedQuery) => {
            updateQueryData(_populatedQuery);

            legacyClient.getStudyFvcSettings().then(stFvcS => {
                setOverreadEnabled(stFvcS && stFvcS.IsOverreadingAvailable);
                setOverreadChallengeProtocol(stFvcS.SelectedFinalTestAcceptabilityDecisionMaker);
                setIsLoading(false);
            });
        });
    }

    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createFvcQC(sessionId, SpirometryAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refreshData();
    }

    const handleReassign = async (sessionId: string, userId: string) => {
        await legacyClient.reassignFvc(sessionId, userId);
        setReassignToUserModalVisible(false);
        refreshData();
    }

    const [queryState, dispatch] = queryTableReducer;

    const [selectedFilterModal, setSelectedFilterModal] = React.useState<string>(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const [isAssignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);

    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);

    const handleFilter = (member: string) => setSelectedFilterModal(member);

    const updateQueryData = (query: Query<SpirometryAnalysisSet>) => dispatch(ReceiveQueryAction.create(query));
    const handlePage = (page: number) => dispatch(PageAction.create(page));
    const handleSort = (member: string) => dispatch(SortAction.create(member));

    const [overreadEnabled, setOverreadEnabled] = React.useState(false);
    const [overreadChallengeProtocol, setOverreadChallengeProtocol] = React.useState<ChallengeDecisionMaker>();
    const subjectTypeFilterMember = "Subject.SubjectTypeId";


    // A change in the query should provoke a re-fetch of the data
    React.useEffect(() => {
        refreshData();
    }, [queryState.query.Page.Current, queryState.query.Order.Member, queryState.query.Order.Direction, queryState.query.Filters]);

    const getModal = (): JSX.Element => {
        switch (selectedFilterModal) {
            case ("Site.SiteNumber"):
                return <SiteIdFilterModal filterMember="Site.SiteNumber" query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case ("Subject.SubjectNumber"):
                return <SubjectIdFilterModal filterMember="Subject.SubjectNumber" query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (subjectTypeFilterMember):
                return <SubjectTypeFilterModal filterMember={subjectTypeFilterMember} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (VisitFilterMember):
                return <VisitFilterModal studyId={study.id} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (SessionFilterMember):
                return <SessionFilterModal studyId={study.id} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case ("StatusId"):
                return <StatusFilterModal query={queryState.query} hasChallengeStatuses={true} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (AnalysisSetTypeFilterMember):
                return <AnalysisSetTypeFilterModal fetcher={legacyClient.getSpirometryAnalysisSetTypes.bind(legacyClient)} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;
            case (CreatedAtFilterMember):
                return <CreatedAtFilterModal query={queryState.query} onClose={((from, to) => {
                    dispatch(FilterAction.create(from.Member, from.Condition, from.Values));
                    dispatch(FilterAction.create(to.Member, to.Condition, to.Values));
                    setSelectedFilterModal(null);
                })} />;
            case (StartedAtFilterMember):
                return <StartedAtFilterModal query={queryState.query} onClose={((from, to) => {
                    dispatch(FilterAction.create(from.Member, from.Condition, from.Values));
                    dispatch(FilterAction.create(to.Member, to.Condition, to.Values));
                    setSelectedFilterModal(null);
                })} />;
            case ("Analyst.Email"):
                return <OverreaderFilterModal filterMember="Analyst.Email" permissionToGetUserFor={PermissionEnum.OverreadFvcSession} query={queryState.query} onClose={((member, condition, values) => {
                    dispatch(FilterAction.create(member, condition, values));
                    setSelectedFilterModal(null);
                })} />;

            default:
                return null;
        }
    }

    return (
        <>
            <QueryTable
                query={queryState.query}
                rowUniqueIdentifier={row => row.Id}
                onPage={handlePage}
                onSort={handleSort}
                onFilter={handleFilter}
                isLoading={isLoading}
            >
                <QueryTableColumn
                    member={"Site.SiteNumber"}
                    header={SiteIdHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.Site.SiteNumber)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={"Subject.SubjectNumber"}
                    header={SubjectIdHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.Subject.SubjectNumber)}
                    sortable={true}
                    filterable={true}
                /> 
                
                <QueryTableColumn
                    member={subjectTypeFilterMember}
                    header={SubjectTypeHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.Subject.SubjectTypeName)}
                    sortable={true}
                    filterable={true}
                /> 
                <QueryTableColumn
                    member={VisitFilterMember}
                    header={VisitHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.Visit.Name)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={SessionFilterMember}
                    header={SessionHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.SessionLabel)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={AnalysisSetTypeFilterMember}
                    header={AnalysisSetTypeHeader}
                    map={(row: SpirometryAnalysisSet) => formatNull(row.Type.Name)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={CreatedAtFilterMember}
                    header={CreatedAtHeader}
                    map={(row: SpirometryAnalysisSet) => formatDateTime(row.CreatedDateTime)}
                    sortable={true}
                    filterable={true}
                />
                <QueryTableColumn
                    member={StartedAtFilterMember}
                    header={StartedAtHeader}
                    map={(row: SpirometryAnalysisSet) => formatDateTime(row.StartedDateTime)}
                    sortable={true}
                    filterable={true}
                />
                {ViewOverreaderName()
                    ? <QueryTableColumn
                        member={"Analyst.Email"}
                        header={OverreaderHeader}
                        map={(row: SpirometryAnalysisSet) => formatNull(row.Analyst ? row.Analyst.Email : null)}
                        sortable={true}
                        filterable={true} />
                    : null }
                <QueryTableColumn
                    member={"StatusId"}
                    header={StatusHeader}
                    map={(row: SpirometryAnalysisSet) => GetAnalysisStatus(row)}
                    sortable={true}
                    filterable={true}
                />
                
                <QueryTableColumn
                    member=""
                    header=""
                    map={(row: SpirometryAnalysisSet) => RenderHamburgerMenu(row, currentUserPermissions, study.id, currentUser, overreadEnabled)}
                    sortable={false}
                    filterable={false}
                />
            </QueryTable>
            {getModal()}

            <AssignToUserInGroupModal show={isAssignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadFvcSession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadFvcSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />
        </>);
};

const mapStateToProps = (state: MainState, ownProps: SpirometryAnalysisSetListTableOwnProps): SpirometryAnalysisSetListTableProps => {
    return {
        queryTableReducer: ownProps.queryTableReducer,
        currentUser: state.security.currentUser
    };
}

export default connect<SpirometryAnalysisSetListTableProps, {}, SpirometryAnalysisSetListTableOwnProps>(mapStateToProps)(SpirometryAnalysisSetListTable);
